/**
 * AI-Powered Beverage Intelligence Service
 *
 * Integrates with Anthropic Claude and other AI services to provide
 * intelligent analysis of beverages including tasting notes generation,
 * food pairing suggestions, and professional recommendations.
 */

import Anthropic from "@anthropic-ai/sdk";

const anthropic = new Anthropic({
  apiKey: process.env.REACT_APP_ANTHROPIC_API_KEY,
  dangerouslyAllowBrowser: true, // Note: In production, this should be handled by a backend
});

/**
 * AI Beverage Intelligence Service
 */
export class AIBeverageService {
  constructor() {
    this.model = "claude-3-haiku-20240307";
    this.maxTokens = 1000;
  }

  /**
   * Generate professional tasting notes for any beverage
   */
  async generateTastingNotes(beverage) {
    const prompt = `As a professional sommelier and beverage expert, provide detailed tasting notes for this beverage:

Name: ${beverage.name}
Type: ${beverage.type || beverage.category || "Unknown"}
${beverage.winery ? `Producer: ${beverage.winery}` : ""}
${beverage.vintage ? `Vintage: ${beverage.vintage}` : ""}
${beverage.price ? `Price: $${beverage.price}` : ""}

Please provide tasting notes in this exact JSON format:
{
  "appearance": "Visual description (color, clarity, etc.)",
  "nose": "Aroma description with specific scent notes",
  "palate": "Taste description including flavors, body, texture",
  "finish": "Aftertaste description and length",
  "overall": "Overall impression and quality assessment",
  "characteristics": ["characteristic1", "characteristic2", "characteristic3"],
  "complexity": 4
}

Make the tasting notes professional, specific, and authentic. Use wine/beverage terminology appropriate for the type of drink. Complexity should be rated 1-5 where 1 is simple and 5 is very complex.`;

    try {
      const response = await anthropic.messages.create({
        model: this.model,
        max_tokens: this.maxTokens,
        messages: [
          {
            role: "user",
            content: prompt,
          },
        ],
      });

      const content = response.content[0].text;
      const jsonMatch = content.match(/\{[\s\S]*\}/);

      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }

      // Fallback if JSON parsing fails
      return this.generateFallbackTastingNotes(beverage);
    } catch (error) {
      console.error("AI tasting notes generation failed:", error);
      return this.generateFallbackTastingNotes(beverage);
    }
  }

  /**
   * Generate food pairing suggestions
   */
  async generateFoodPairings(beverage, restaurantMenu = null) {
    let menuContext = "";
    if (restaurantMenu) {
      const menuItems = restaurantMenu
        .map((item) => `- ${item.name}: ${item.description || ""}`)
        .join("\n");
      menuContext = `\n\nRestaurant Menu Items:\n${menuItems}`;
    }

    const prompt = `As a professional sommelier, suggest food pairings for this beverage:

Name: ${beverage.name}
Type: ${beverage.type || beverage.category || "Unknown"}
${beverage.description ? `Description: ${beverage.description}` : ""}
${menuContext}

Please provide pairing suggestions in this exact JSON format:
{
  "classic_pairings": ["pairing1", "pairing2", "pairing3"],
  ${
    restaurantMenu
      ? '"menu_pairings": [{"item": "menu_item_name", "reason": "why it pairs well"}],'
      : ""
  }
  "flavor_reasons": ["reason1", "reason2", "reason3"],
  "occasion_suggestions": ["occasion1", "occasion2"],
  "serving_recommendations": {
    "temperature": "ideal serving temperature",
    "glassware": "recommended glass type",
    "garnish": "optional garnish or accompaniment"
  }
}

Focus on specific, actionable pairing advice that would help restaurant staff make recommendations.`;

    try {
      const response = await anthropic.messages.create({
        model: this.model,
        max_tokens: this.maxTokens,
        messages: [
          {
            role: "user",
            content: prompt,
          },
        ],
      });

      const content = response.content[0].text;
      const jsonMatch = content.match(/\{[\s\S]*\}/);

      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }

      return this.generateFallbackPairings(beverage);
    } catch (error) {
      console.error("AI food pairing generation failed:", error);
      return this.generateFallbackPairings(beverage);
    }
  }

  /**
   * Generate professional beverage description and marketing copy
   */
  async generateBeverageDescription(beverage) {
    const prompt = `As a professional beverage marketer and sommelier, write an engaging description for this beverage that would appeal to restaurant customers:

Name: ${beverage.name}
Type: ${beverage.type || beverage.category || "Unknown"}
${beverage.winery ? `Producer: ${beverage.winery}` : ""}
${beverage.price ? `Price: $${beverage.price}` : ""}
${beverage.description ? `Current Description: ${beverage.description}` : ""}

Please provide marketing copy in this exact JSON format:
{
  "short_description": "1-2 sentence engaging description for menu",
  "detailed_description": "Longer description for wine list or digital menu",
  "selling_points": ["unique_feature1", "unique_feature2", "unique_feature3"],
  "target_customer": "ideal customer type (e.g., 'wine enthusiast', 'casual diner')",
  "upsell_suggestion": "suggestion for staff to increase sales"
}

Make the language appealing but not overly technical. Focus on what makes this beverage special and worth ordering.`;

    try {
      const response = await anthropic.messages.create({
        model: this.model,
        max_tokens: this.maxTokens,
        messages: [
          {
            role: "user",
            content: prompt,
          },
        ],
      });

      const content = response.content[0].text;
      const jsonMatch = content.match(/\{[\s\S]*\}/);

      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }

      return this.generateFallbackDescription(beverage);
    } catch (error) {
      console.error("AI description generation failed:", error);
      return this.generateFallbackDescription(beverage);
    }
  }

  /**
   * Analyze beverage and suggest professional rating
   */
  async generateProfessionalAssessment(beverage, externalData = null) {
    const externalContext = externalData
      ? `\n\nExternal Data Available:\n${JSON.stringify(externalData, null, 2)}`
      : "";

    const prompt = `As a professional beverage critic, assess this beverage and provide a professional rating and review:

Name: ${beverage.name}
Type: ${beverage.type || beverage.category || "Unknown"}
${beverage.winery ? `Producer: ${beverage.winery}` : ""}
${beverage.vintage ? `Vintage: ${beverage.vintage}` : ""}
${beverage.price ? `Price: $${beverage.price}` : ""}
${externalContext}

Please provide assessment in this exact JSON format:
{
  "rating": {
    "score": 88,
    "scale": 100,
    "source": "SommelAI Professional Assessment"
  },
  "review_summary": "Professional review in 2-3 sentences",
  "quality_indicators": ["indicator1", "indicator2", "indicator3"],
  "value_assessment": "excellent|good|fair|poor value for price",
  "recommended_for": ["customer_type1", "customer_type2"],
  "cellaring_potential": "drink now, 2-5 years, 5+ years, etc.",
  "confidence_level": 0.85
}

Base the rating on quality, typicity, and value. Score should be 75-100 range where 90+ is exceptional, 85-89 is very good, 80-84 is good, 75-79 is average.`;

    try {
      const response = await anthropic.messages.create({
        model: this.model,
        max_tokens: this.maxTokens,
        messages: [
          {
            role: "user",
            content: prompt,
          },
        ],
      });

      const content = response.content[0].text;
      const jsonMatch = content.match(/\{[\s\S]*\}/);

      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }

      return this.generateFallbackAssessment(beverage);
    } catch (error) {
      console.error("AI professional assessment failed:", error);
      return this.generateFallbackAssessment(beverage);
    }
  }

  /**
   * Generate customer recommendations based on preferences
   */
  async generateRecommendations(customerProfile, availableBeverages) {
    const prompt = `As a professional sommelier, recommend beverages from this restaurant's collection based on the customer's preferences:

Customer Profile:
${JSON.stringify(customerProfile, null, 2)}

Available Beverages:
${availableBeverages
  .map(
    (b) =>
      `- ${b.name} (${b.type}) - $${b.price} - ${
        b.description || "No description"
      }`
  )
  .join("\n")}

Please provide recommendations in this exact JSON format:
{
  "primary_recommendations": [
    {
      "beverage_name": "exact name from list",
      "match_score": 0.95,
      "reason": "why this matches their preferences",
      "serving_suggestion": "how to present/serve it"
    }
  ],
  "alternative_options": [
    {
      "beverage_name": "exact name from list", 
      "reason": "why this is a good alternative"
    }
  ],
  "discovery_suggestion": {
    "beverage_name": "exact name from list",
    "reason": "why this would expand their palate"
  }
}

Focus on matching their stated preferences while also suggesting one item that might expand their taste experience.`;

    try {
      const response = await anthropic.messages.create({
        model: this.model,
        max_tokens: this.maxTokens,
        messages: [
          {
            role: "user",
            content: prompt,
          },
        ],
      });

      const content = response.content[0].text;
      const jsonMatch = content.match(/\{[\s\S]*\}/);

      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }

      return this.generateFallbackRecommendations(availableBeverages);
    } catch (error) {
      console.error("AI recommendations generation failed:", error);
      return this.generateFallbackRecommendations(availableBeverages);
    }
  }

  // Fallback methods for when AI fails
  generateFallbackTastingNotes(beverage) {
    const type = beverage.type || beverage.category || "beverage";
    return {
      appearance: `Clear, appealing ${type} with good presentation`,
      nose: `Pleasant aromas characteristic of quality ${type}`,
      palate: `Well-balanced flavors with good structure`,
      finish: `Clean, satisfying finish`,
      overall: `A quality ${type} that represents its style well`,
      characteristics: ["balanced", "well-made", "enjoyable"],
      complexity: 3,
    };
  }

  generateFallbackPairings(beverage) {
    const type = beverage.type || beverage.category || "beverage";
    return {
      classic_pairings: [
        `foods that complement ${type}`,
        "seasonal dishes",
        "cheese selections",
      ],
      flavor_reasons: [
        "complementary flavors",
        "balanced intensity",
        "traditional pairing",
      ],
      occasion_suggestions: ["dinner", "special occasions"],
      serving_recommendations: {
        temperature: "properly chilled",
        glassware: "appropriate glassware",
        garnish: "none required",
      },
    };
  }

  generateFallbackDescription(beverage) {
    return {
      short_description: `A quality ${
        beverage.type || "beverage"
      } that's perfect for any occasion`,
      detailed_description: `This ${
        beverage.type || "beverage"
      } offers excellent value and pairs well with our menu`,
      selling_points: ["quality selection", "great value", "versatile pairing"],
      target_customer: "all customers",
      upsell_suggestion: "Recommend pairing with our chef specials",
    };
  }

  generateFallbackAssessment(beverage) {
    return {
      rating: {
        score: 82,
        scale: 100,
        source: "SommelAI Estimated Rating",
      },
      review_summary: `A solid ${
        beverage.type || "beverage"
      } that offers good quality and value`,
      quality_indicators: ["well-made", "consistent", "good value"],
      value_assessment: "good",
      recommended_for: ["regular customers", "newcomers to type"],
      cellaring_potential: "drink now",
      confidence_level: 0.6,
    };
  }

  generateFallbackRecommendations(beverages) {
    const primary = beverages[0] || { name: "No beverages available" };
    return {
      primary_recommendations: [
        {
          beverage_name: primary.name,
          match_score: 0.7,
          reason: "Popular choice with broad appeal",
          serving_suggestion: "Serve at appropriate temperature",
        },
      ],
      alternative_options: beverages.slice(1, 3).map((b) => ({
        beverage_name: b.name,
        reason: "Another excellent option",
      })),
      discovery_suggestion:
        beverages.length > 3
          ? {
              beverage_name: beverages[3].name,
              reason: "Try something new",
            }
          : null,
    };
  }
}

/**
 * Export singleton instance
 */
export const aiBeverageService = new AIBeverageService();
